import { useState } from "react";
import { toast } from "sonner";
import LegalLayout, { LegalSection } from "../components/LegalLayout";

const SUPPORT_EMAIL = "support@evervow.me";

const buildMailto = (email, reason) => {
  const subject = encodeURIComponent("Account deletion request");
  const body = encodeURIComponent(
    `Hi Evervow team,\n\nPlease delete my Evervow account and all associated data.\n\nAccount email: ${email || "<the email address I use to sign in>"}\nReason (optional): ${reason || "-"}\n\nThanks.`
  );
  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
};

export default function DeleteAccountPage() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your account email.");
      return;
    }
    window.location.href = buildMailto(email.trim(), reason.trim());
    toast.success("Opening your email client…", {
      description: `If nothing opens, email us directly at ${SUPPORT_EMAIL}.`,
    });
  };

  return (
    <LegalLayout title="Delete your account" updated="July 3, 2026" testid="delete-account-page">
      <p>
        You can request permanent deletion of your Evervow account and all associated personal data
        at any time. This page exists so you can start that process from outside the app — no login
        required.
      </p>

      <LegalSection heading="What gets deleted">
        <ul className="list-disc pl-6 space-y-2">
          <li>Your profile (name, age, location, bio, photos, values, preferences).</li>
          <li>Your likes, passes, and mutual matches.</li>
          <li>Messages you sent and received on Evervow.</li>
          <li>Your Premium subscription record and payment metadata that we control (Stripe retains its own transaction records under its policy).</li>
        </ul>
      </LegalSection>

      <LegalSection heading="What we may keep (limited, for legal reasons)">
        <ul className="list-disc pl-6 space-y-2">
          <li>Records of banned accounts, to prevent re-registration by users who violated our Terms.</li>
          <li>Basic accounting/tax records of Premium purchases, as required by law.</li>
          <li>Information we are legally required to preserve in response to lawful requests.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="How to request deletion">
        <p>
          Fill out the short form below and press <strong>Send deletion request</strong>. Your email
          client will open pre-filled with a message to{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-[#7A3B3F] underline underline-offset-4"
            data-testid="support-mailto-link"
          >
            {SUPPORT_EMAIL}
          </a>
          . Send it as-is and we will process your request within 30 days and email you a
          confirmation when it is complete.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4 bg-[#F3F0EA] border border-[#E5E0D8] p-6 sm:p-8">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]">
              Account email
            </label>
            <input
              data-testid="delete-account-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-3 w-full bg-transparent border-b border-[#E5E0D8] rounded-none px-0 py-3 focus:outline-none focus:border-[#7A3B3F] transition-colors duration-300 placeholder:text-[#8A8A8A]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]">
              Reason (optional)
            </label>
            <textarea
              data-testid="delete-account-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Anything you'd like us to know — this helps us improve Evervow."
              className="mt-3 w-full bg-transparent border-b border-[#E5E0D8] rounded-none px-0 py-3 focus:outline-none focus:border-[#7A3B3F] transition-colors duration-300 placeholder:text-[#8A8A8A] min-h-[100px] resize-none"
            />
          </div>
          <div className="pt-2 flex flex-wrap gap-4 items-center">
            <button
              type="submit"
              data-testid="delete-account-submit-button"
              className="bg-[#7A3B3F] text-[#FAF9F6] px-8 py-3 hover:bg-[#5D2B2E] transition-colors duration-300"
            >
              Send deletion request
            </button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Account%20deletion%20request`}
              data-testid="delete-account-direct-mailto"
              className="text-sm text-[#5C5C5C] underline underline-offset-4 hover:text-[#1A1A1A] transition-colors duration-300"
            >
              Or email {SUPPORT_EMAIL} directly
            </a>
          </div>
        </form>
      </LegalSection>

      <LegalSection heading="Prefer to delete from inside the app?">
        <p>
          If you are already signed in, you can also request deletion by emailing{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Account%20deletion%20request`}
            className="text-[#7A3B3F] underline underline-offset-4"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          from the address associated with your account. We will verify ownership before processing
          the request.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
